/**
 * F-159: the referral code a visitor arrived with (`?ref=<code>`), kept for 30
 * days with the date it was seen. Signup and the request to join send both
 * (`referralCode`, `referralSeenAt`); the server decides whether it counts
 * and ignores anything older than 30 days, so this only carries it there.
 */
const STORAGE_KEY = 'memberfy_referral'
const TTL_MS = 30 * 24 * 60 * 60 * 1000
const CODE_PATTERN = /^[A-Za-z0-9_-]{1,64}$/

export interface StoredReferral {
  code: string
  seenAt: string
}

const read = (): StoredReferral | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const value = JSON.parse(raw) as Partial<StoredReferral>
    if (typeof value.code !== 'string' || typeof value.seenAt !== 'string') return null
    const seen = Date.parse(value.seenAt)
    if (!Number.isFinite(seen) || Date.now() - seen > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY)
      return null
    }
    return { code: value.code, seenAt: value.seenAt }
  } catch {
    return null
  }
}

/**
 * Reads `?ref=` from the address, stores it (a new link replaces an older one)
 * and takes it out of the address bar, so a shared or bookmarked page does not
 * carry someone else's code along.
 */
export function captureReferralFromUrl(): void {
  try {
    const url = new URL(window.location.href)
    const code = url.searchParams.get('ref')?.trim()
    if (!code) return
    if (CODE_PATTERN.test(code)) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ code, seenAt: new Date().toISOString() }))
    }
    url.searchParams.delete('ref')
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
  } catch {
    /* No storage (private mode) or no history: the signup just goes without a referral. */
  }
}

/** The body fields signup and the request to join add; empty when there is no live referral. */
export function referralSignupFields(): { referralCode?: string; referralSeenAt?: string } {
  const stored = read()
  return stored ? { referralCode: stored.code, referralSeenAt: stored.seenAt } : {}
}

/** After a signup or request went through: the code was used. */
export function clearReferral(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* nothing stored */
  }
}
